import { useState, useEffect, useCallback } from 'react';
import { MapCard1 } from '../components_maps/dev/MapCard1';
import { MapListToolbar } from '../components_maps/MapListToolbar';
import { MapListTable, MapListItem } from '../components_maps/MapListTable';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { SaveAndRefresh } from '../components_misc/SaveAndRefresh';
import { Navigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { refreshMyMilestoneMaps, deleteMilestoneMap, updateUser } from '../store/map3PendingSlice';
import {
  filterMapsByTitle,
  getViewModePreference,
  setViewModePreference,
  ViewMode,
} from '../utils/mapListingUtils';

export function MyMaps() {
  const dispatch = useDispatch<AppDispatch>();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const { userName } = useParams<{ userName: string }>();
  useDocumentTitle('My Milestone Maps');

  // Search and view state
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(() => getViewModePreference());

  // Persist view mode preference
  useEffect(() => {
    setViewModePreference(viewMode);
  }, [viewMode]);

  // Auto-refresh maps on mount so the list reflects any newly created maps
  useEffect(() => {
    dispatch(refreshMyMilestoneMaps());
  }, [dispatch]);

  const handleMyMapsRefresh = async () => {
    await dispatch(refreshMyMilestoneMaps());
  };

  // Handlers for table actions
  const handleStar = useCallback(
    async (shortId: string) => {
      if (!userProfile.myMilestoneMaps) return;
      const map = userProfile.myMilestoneMaps.find((m) => m.shortId === shortId);
      if (!map) return;

      const updatedMaps = userProfile.myMilestoneMaps.map((m) =>
        m.shortId === shortId ? { ...m, starred: !m.starred } : m
      );

      await dispatch(
        updateUser({
          uid: userProfile.uid,
          updates: { myMilestoneMaps: updatedMaps },
        })
      );
    },
    [dispatch, userProfile.uid, userProfile.myMilestoneMaps]
  );

  const handleDelete = useCallback(
    async (shortId: string) => {
      await dispatch(deleteMilestoneMap(shortId)).unwrap();
    },
    [dispatch]
  );

  const handleCopyLink = useCallback(
    (shortId: string, ownerName: string) => {
      const url = `${window.location.origin}/${ownerName}/maps/${shortId}`;
      navigator.clipboard.writeText(url);
    },
    []
  );

  // If userProfile data is still loading, show a loading state
  if (!userProfile || !userProfile.userName) {
    return (
      <section className="relative mx-auto lg:max-w-7xl" aria-label="My milestone maps">
        <div className="pb-40 pt-2">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-xl font-semibold">My Milestone Maps</h1>
          </div>
          <div className="text-center py-12" role="status" aria-live="polite">
            <p className="text-gray-500">Loading your milestone maps...</p>
          </div>
        </div>
      </section>
    );
  }

  // Once data is loaded, check if current user is the owner of this shared maps page
  if (userName !== userProfile.userName) {
    return <Navigate to="/access-denied" replace />;
  }

  const maps: MapListItem[] =
    userProfile.myMilestoneMaps
      ?.map((map) => ({
        shortId: map.shortId,
        title: map.title,
        ownerName: userProfile.userName,
        starred: map.starred || false,
      }))
      .sort((a, b) => {
        // Sort by starred status first (starred items come first)
        if (a.starred && !b.starred) return -1;
        if (!a.starred && b.starred) return 1;
        // Then sort by title
        return a.title.localeCompare(b.title);
      }) || [];

  // Filter maps by search term
  const filteredMaps = filterMapsByTitle(maps, searchTerm);

  // For card view, add the "+" card at the beginning (only when not searching)
  const cardMaps = searchTerm.trim()
    ? filteredMaps
    : [
        {
          shortId: '0',
          title: '+',
          ownerName: userProfile.userName,
          starred: false,
        },
        ...filteredMaps,
      ];

  return (
    <section className="relative mx-auto lg:max-w-7xl" aria-label="My milestone maps">
      <div className="pb-40 pt-2">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold">My Milestone Maps</h1>
          <div className="flex items-center gap-4">
            <MapListToolbar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
            <SaveAndRefresh clickToRefresh={true} onRefresh={handleMyMapsRefresh} />
          </div>
        </div>

        {/* Empty state when search has no results */}
        {filteredMaps.length === 0 && searchTerm.trim() ? (
          <div className="text-center py-12" role="status" aria-live="polite">
            <p className="text-gray-500">No maps match your search</p>
          </div>
        ) : viewMode === 'list' ? (
          /* List view */
          <MapListTable
            maps={filteredMaps}
            onStar={handleStar}
            onDelete={handleDelete}
            onCopyLink={handleCopyLink}
          />
        ) : (
          /* Card view */
          <div className="grid gap-4 grid-cols-2" role="grid" aria-label="Milestone maps grid">
            {cardMaps.map((map) => (
              <div key={map.shortId} role="gridcell">
                <MapCard1
                  mapShortId={map.shortId}
                  title={map.title}
                  ownerName={map.ownerName}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
